import {
  FC,
  type InputEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { Input } from 'antd';
import { useFormContext } from 'react-hook-form';
import { useGetIdFromName } from '../../../../../../common/form';
import { AlgorithmSourceFormDataType } from '../../../../sources/schema/types';
import type { AlgorithmJoinsAndMappingsGraphValidFormDataType } from '../../../schema/types';
import {
  decodeDimensionCodeValue,
  encodeDimensionCodeValue,
} from '../../../utils';
import { AlgorithmJoinsWithMappingsGraphContext } from '../../AlgorithmJoinsWithMappingsGraphContext';

interface DbSourceDimensionColumnFormFieldProps {
  name: `nodes.${number}.data.items.${number}.dimensionCode`;
  algorithmSource: AlgorithmSourceFormDataType;
  value: string;
  readOnly?: boolean;
}

export const DbSourceDimensionColumnFormInput: FC<
  DbSourceDimensionColumnFormFieldProps
> = (props) => {
  const { name, value, algorithmSource, readOnly } = props;
  const getIdFromName = useGetIdFromName();

  const { algorithmSourcesById } = useContext(
    AlgorithmJoinsWithMappingsGraphContext,
  );

  const { aliasOrCode, columnName } = useMemo(() => {
    if (!value) {
      return { aliasOrCode: '', columnName: '' };
    }

    const { algorithmSourceId, dimensionCode } =
      decodeDimensionCodeValue(value);

    const { alias, dataSource } = algorithmSourcesById.get(
      algorithmSourceId,
    ) as Extract<AlgorithmSourceFormDataType, { sourceType: 'DB' }>;

    return { aliasOrCode: alias || dataSource.code, columnName: dimensionCode };
  }, [value, algorithmSourcesById]);

  const { setValue } =
    useFormContext<AlgorithmJoinsAndMappingsGraphValidFormDataType>();

  const handleChange = useCallback(
    (algorithmSourceId: number, columnName: string) => {
      setValue(name, encodeDimensionCodeValue(algorithmSourceId, columnName));
    },
    [name, setValue],
  );

  useEffect(() => {
    handleChange(algorithmSource.id, columnName);
  }, [handleChange, algorithmSource, columnName]);

  if (readOnly) {
    return `${aliasOrCode} | ${columnName}`;
  }

  return (
    <Input
      id={getIdFromName(name)}
      className="nodrag"
      style={{ width: '100%' }}
      addonBefore={aliasOrCode}
      value={columnName}
      onChange={(event) => {
        const columnName = event.target.value.replace(/\./g, '');
        handleChange(algorithmSource.id, columnName);
      }}
      onBeforeInput={(event: InputEvent<HTMLInputElement>) => {
        if (event.data === '.') {
          event.preventDefault();
        }
      }}
    />
  );
};
